import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { AUDIT_REASON_MIN_LENGTH } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { reasonProblem, reasonReady } from './payout-state.ts';

/**
 * THE STOP, said where the funds screen's figures are, and the one way to lift it.
 *
 * THE WRITE:
 *   POST /api/payouts/breaker/reset   { reason }, behind funds.approve
 *
 * StarPay refusing the platform's credentials trips the stop for every brand at once, and nothing
 * is handed to StarPay until a person restarts it — releases on a StarPay rail are refused
 * meanwhile, saying so. The stop is one record the API and the worker both read before every
 * hand-over, so restarting is the platform's one switch, whatever brand the operator is looking at.
 * An audited act, so it carries a reason at the database's own floor.
 *
 * Restarting is not dismissible, and danger rather than warn: the press puts every brand's payouts
 * back in StarPay's hands at once, and if the fault is still there the next hand-over is refused and
 * the stop trips again — so it is said before the press, beside the reason.
 */
export function PayoutStop({
  tripped,
  canRestart,
  role,
  onRestarted,
}: {
  /** Whether payouts are stopped now, as the funds summary read it. */
  tripped: boolean;
  /** Whether this admin holds funds.approve, which restarting needs. */
  canRestart: boolean;
  role: string;
  /** After payouts were restarted, so the band is read again — which is where the stop is shown. */
  onRestarted: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [restarting, setRestarting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const restart = async () => {
    if (!reasonReady(reason) || busy) return;
    setReasonError(undefined);
    setBusy(true);
    try {
      await api.post<{ tripped: boolean }>('/api/payouts/breaker/reset', { reason: reason.trim() });
      toast('Payouts restarted. Releases on a StarPay rail are handed to StarPay again.');
      setRestarting(false);
      setReason('');
      onRestarted();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['reason']) {
        setReasonError(error.fields['reason']);
        return;
      }
      showError({
        title: 'Payouts were not restarted',
        message:
          error instanceof ApiRequestError ? error.message : 'Payouts are still stopped. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {tripped ? (
        <div className="notice notice-danger">
          <Icon name="warning-octagon" />
          <div>
            <strong>
              Payouts are stopped: StarPay refused the platform&rsquo;s credentials or configuration.
              Nothing is sent until an operator restarts them.
            </strong>
            <div className="row" style={{ marginTop: 'var(--space-2)' }}>
              {canRestart ? (
                <Button
                  small
                  variant="primary"
                  onClick={() => {
                    setReason('');
                    setReasonError(undefined);
                    setRestarting(true);
                  }}
                >
                  Restart payouts
                </Button>
              ) : (
                <span className="text-subtle" style={{ fontSize: '11.5px' }}>
                  Restarting them needs <span className="signin-perm">funds.approve</span>, which{' '}
                  {role} does not hold.
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <Dialog
        open={restarting}
        onClose={() => setRestarting(false)}
        title="Restart payouts?"
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setRestarting(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={busy || !reasonReady(reason)}
              onClick={() => void restart()}
            >
              Restart payouts
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              <strong>Payouts go to StarPay again, for every brand, the moment you confirm</strong> —
              new releases on a StarPay rail and the platform&rsquo;s own re-sends alike. Payouts
              already waiting for an operator stay waiting. If the credentials or the configuration
              are not actually fixed, the next hand-over is refused the same way and payouts stop
              again.
            </div>
          </div>

          <Field
            label="Reason"
            error={reasonError ?? reasonProblem(reason)}
            hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters — what was fixed, and who confirmed it with StarPay.`}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                minLength={AUDIT_REASON_MIN_LENGTH}
                maxLength={500}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError(undefined);
                }}
                placeholder="StarPay re-issued the merchant key; confirmed with their support desk."
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
